import { axiosBaseQuery } from '@api/api';
import { createApi } from '@reduxjs/toolkit/query/react';

export interface Channel {
  _id: string;
  channelId?: string; // UUID from backend
  name: string;
  projectId: string;
  createdAt: string;
  createdByDisplayName?: string | null;
  updatedAt?: string;
  bundlesCount?: number;
  activeBundlesCount?: number;
  expiredBundlesCount?: number;
  activeBundleStorageBytes?: number;
  managedPublishing?: boolean;
  isProduction?: boolean;
  productionBundleExpiryDays?: number | null;
  isFavorite?: boolean;
  managedPublishingRolloutSummary?: {
    activePartialRolloutCount: number;
    hasActivePartialRollouts: boolean;
  };
}

export type ChannelSortBy = 'createdAt' | 'lastUpdated';

export interface GetChannelsByProjectIdRequest {
  projectId: string;
  limit?: number;
  cursor?: string;
  sortBy?: ChannelSortBy;
}

export interface SearchChannelsByProjectIdRequest {
  projectId: string;
  query?: string;
  name?: string;
  limit?: number;
  cursor?: string;
  sortBy?: ChannelSortBy;
}

export interface ChannelListResponse {
  items: Channel[];
  nextCursor: string | null;
  hasMore: boolean;
}

export const normalizeChannelListResponse = (response: unknown): ChannelListResponse => {
  if (Array.isArray(response)) {
    return {
      items: response as Channel[],
      nextCursor: null,
      hasMore: false,
    };
  }

  const typed = response as Partial<ChannelListResponse> | null | undefined;
  return {
    items: Array.isArray(typed?.items) ? typed.items : [],
    nextCursor: typed?.nextCursor ?? null,
    hasMore: Boolean(typed?.hasMore),
  };
};

export const getChannelsFromResult = (
  result?: ChannelListResponse | Channel[] | null
): Channel[] => {
  if (!result) {
    return [];
  }

  if (Array.isArray(result)) {
    return result;
  }

  return Array.isArray(result.items) ? result.items : [];
};

export interface SetManagedPublishingRequest {
  channelId: string;
  managedPublishing: boolean;
}

export interface SetManagedPublishingResponse {
  channelId: string;
  managedPublishing: boolean;
}

export interface SetProductionChannelRequest {
  channelId: string;
  productionBundleExpiryDays: number | null;
}

export interface SetProductionChannelResponse {
  channelId: string;
  isProduction: boolean;
  productionBundleExpiryDays: number | null;
}

export interface ProductionChannelPreviewBundle {
  hash: string;
  bundleVersion: number;
  version: string;
  uploadedAt: string;
  currentExpiresAt: string | null;
  normalizedExpiresAt: string | null;
  willExpireImmediately: boolean;
  channelIds: string[];
}

export interface PreviewUnsetProductionChannelRequest {
  channelId: string;
}

export interface PreviewUnsetProductionChannelResponse {
  channelId: string;
  channelName: string;
  productionBundleExpiryDays: number | null;
  affectedBundlesCount: number;
  immediatelyExpiredBundlesCount: number;
  affectedBundles: ProductionChannelPreviewBundle[];
  hasMoreAffectedBundles: boolean;
}

export interface UnsetProductionChannelResponse {
  channelId: string;
  isProduction: boolean;
  productionBundleExpiryDays: null;
  affectedBundlesCount: number;
  immediatelyExpiredBundlesCount: number;
}

export interface ChannelCleanupInlineResult {
  totalFound: number;
  detachedFromDeletedChannel: number;
  deletedBundles: number;
  deletedObjects: number;
}

export interface ChannelCleanupStatusHelpers {
  state?: string;
  isDone?: boolean;
  isSuccess?: boolean;
  failedReason?: string | null;
}

export type DeleteChannelCleanup =
  | {
      mode: 'queued';
      jobId: string;
    } & ChannelCleanupStatusHelpers
  | {
      mode: 'inline';
      result: ChannelCleanupInlineResult;
    } & ChannelCleanupStatusHelpers;

export interface DeleteChannelResponse {
  _id?: string;
  channelId?: string;
  name?: string;
  projectId?: string;
  cleanup?: DeleteChannelCleanup;
  message?: string;
}

export interface ToggleChannelFavoriteResponse {
  channelId: string;
  projectId: string;
  isFavorite: boolean;
}

export interface AddBundleToChannelRequest {
  channelId: string;
  bundleHash: string;
}

export interface AddBundleToChannelResponse {
  channelId: string;
  name: string;
  projectId: string;
  bundleVersion: number;
  targetHighestBundleVersion: number | null;
  willBeActive: boolean;
}

export const channelsApi = createApi({
  reducerPath: 'channelsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Channels'],
  endpoints: builder => ({
    getChannelsByProjectId: builder.query<ChannelListResponse, GetChannelsByProjectIdRequest>({
      query: ({ projectId, limit, cursor, sortBy }) => ({
        url: '/channels/getByProjectId',
        params: { projectId, limit, cursor, sortBy },
      }),
      transformResponse: response => normalizeChannelListResponse(response),
      providesTags: result => {
        const items = getChannelsFromResult(result);
        return [
          ...items.map(channel => ({
            type: 'Channels' as const,
            id: channel.channelId || channel._id,
          })),
          { type: 'Channels' as const, id: 'LIST' },
        ];
      },
    }),
    searchChannelsByProjectId: builder.query<ChannelListResponse, SearchChannelsByProjectIdRequest>({
      query: ({ projectId, query, name, limit, cursor, sortBy }) => ({
        url: '/channels/searchByProjectId',
        params: { projectId, query, name, limit, cursor, sortBy },
      }),
      transformResponse: response => normalizeChannelListResponse(response),
      providesTags: result => {
        const items = getChannelsFromResult(result);
        return [
          ...items.map(channel => ({
            type: 'Channels' as const,
            id: channel.channelId || channel._id,
          })),
          { type: 'Channels' as const, id: 'LIST' },
        ];
      },
    }),
    createChannel: builder.mutation<Channel, { name: string; projectId: string }>({
      query: body => ({
        url: '/channels/create',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: [{ type: 'Channels', id: 'LIST' }],
    }),
    deleteChannel: builder.mutation<DeleteChannelResponse, { channelId: string }>({
      query: ({ channelId }) => ({
        url: '/channels',
        method: 'DELETE',
        params: { channelId },
      }),
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: 'Channels', id: channelId },
        { type: 'Channels', id: 'LIST' },
      ],
    }),
    setManagedPublishing: builder.mutation<
      SetManagedPublishingResponse,
      SetManagedPublishingRequest
    >({
      query: body => ({
        url: '/channels/setManagedPublishing',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: 'Channels', id: channelId },
        { type: 'Channels', id: 'LIST' },
      ],
    }),
    setProductionChannel: builder.mutation<
      SetProductionChannelResponse,
      SetProductionChannelRequest
    >({
      query: body => ({
        url: '/channels/setProduction',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: 'Channels', id: channelId },
        { type: 'Channels', id: 'LIST' },
      ],
    }),
    previewUnsetProductionChannel: builder.query<
      PreviewUnsetProductionChannelResponse,
      PreviewUnsetProductionChannelRequest
    >({
      query: ({ channelId }) => ({
        url: '/channels/production/unset-preview',
        params: { channelId },
      }),
    }),
    unsetProductionChannel: builder.mutation<
      UnsetProductionChannelResponse,
      { channelId: string }
    >({
      query: body => ({
        url: '/channels/unsetProduction',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: 'Channels', id: channelId },
        { type: 'Channels', id: 'LIST' },
      ],
    }),
    addChannelToFavorites: builder.mutation<ToggleChannelFavoriteResponse, { channelId: string }>({
      query: ({ channelId }) => ({
        url: '/channels/favorites',
        method: 'POST',
        data: { channelId },
      }),
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: 'Channels', id: channelId },
        { type: 'Channels', id: 'LIST' },
      ],
    }),
    removeChannelFromFavorites: builder.mutation<
      ToggleChannelFavoriteResponse,
      { channelId: string }
    >({
      query: ({ channelId }) => ({
        url: '/channels/favorites',
        method: 'DELETE',
        params: { channelId },
      }),
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: 'Channels', id: channelId },
        { type: 'Channels', id: 'LIST' },
      ],
    }),
    addBundleToChannel: builder.mutation<AddBundleToChannelResponse, AddBundleToChannelRequest>({
      query: body => ({
        url: '/channels/addBundleToChannel',
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: (_result, _error, { channelId }) => [
        { type: 'Channels', id: channelId },
        { type: 'Channels', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetChannelsByProjectIdQuery,
  useSearchChannelsByProjectIdQuery,
  useCreateChannelMutation,
  useDeleteChannelMutation,
  useSetManagedPublishingMutation,
  useSetProductionChannelMutation,
  useLazyPreviewUnsetProductionChannelQuery,
  useUnsetProductionChannelMutation,
  useAddChannelToFavoritesMutation,
  useRemoveChannelFromFavoritesMutation,
  useAddBundleToChannelMutation,
} = channelsApi;
